import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  Button,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Hidden,
  Divider,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
  },
  statusActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  statusInactive: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  subtitle: {
    marginBottom: theme.spacing(2),
    color: '#666',
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#666',
  },
  // Mobile card styles
  mobileCard: {
    marginBottom: theme.spacing(2),
  },
  mobileCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  mobileCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.5, 0),
  },
  mobileLabel: {
    color: '#666',
    fontSize: '0.85rem',
  },
  mobileValue: {
    fontWeight: 500,
    fontSize: '0.9rem',
    textAlign: 'right',
    wordBreak: 'break-all',
  },
  dpUserId: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: '#1a1a2e',
  },
  providerChip: {
    backgroundColor: '#1a1a2e',
    color: '#fff',
  },
  loginButton: {
    marginTop: theme.spacing(1.5),
    width: '100%',
  },
  statusContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#1a1a2e',
    color: '#fff',
    fontWeight: 600,
  },
}))(TableCell);

function Credentials() {
  const classes = useStyles();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [loading, setLoading] = useState(true);
  const [credentials, setCredentials] = useState([]);
  const [tokenStatuses, setTokenStatuses] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await apiService.getCredentials();
      setCredentials(data);

      const statuses = {};
      for (const cred of data) {
        try {
          const status = await apiService.getTokenStatus(cred.dp_user_id);
          statuses[cred.dp_user_id] = status.token_status;
        } catch (e) {
          statuses[cred.dp_user_id] = 'NOT_ACTIVE';
        }
      }
      setTokenStatuses(statuses);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const refreshAfterBrokerLogin = (event) => {
      if (event.data && event.data.type === 'MGM_BROKER_TOKEN_UPDATED') {
        fetchData();
      }
    };
    const refreshOnFocus = () => fetchData();

    window.addEventListener('message', refreshAfterBrokerLogin);
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.removeEventListener('message', refreshAfterBrokerLogin);
      window.removeEventListener('focus', refreshOnFocus);
    };
    // Register browser-level callback listeners once for this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (dpUserId) => {
    try {
      const result = await apiService.generateToken(dpUserId);
      if (result.token_url) {
        const popup = window.open(result.token_url, 'mgm-broker-login');
        if (!popup) {
          throw new Error('Broker login popup was blocked by the browser.');
        }
      }
    } catch (error) {
      console.error('Error generating token:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN');
  };

  const renderStatusChip = (dpUserId) => {
    const isActive = tokenStatuses[dpUserId] === 'ACTIVE';
    return (
      <Box className={classes.statusContainer}>
        {isActive ? (
          <CheckCircleIcon style={{ color: '#2e7d32', marginRight: 8, fontSize: 20 }} />
        ) : (
          <ErrorIcon style={{ color: '#c62828', marginRight: 8, fontSize: 20 }} />
        )}
        <Chip
          size="small"
          label={tokenStatuses[dpUserId] || 'Unknown'}
          className={isActive ? classes.statusActive : classes.statusInactive}
        />
      </Box>
    );
  };

  // Mobile Card View
  const renderMobileCard = (cred) => (
    <Card key={cred.id} className={classes.mobileCard}>
      <CardContent>
        <Box className={classes.mobileCardHeader}>
          <Typography className={classes.dpUserId}>
            {cred.dp_user_id}
          </Typography>
          <Chip
            size="small"
            label={cred.app_provider}
            className={classes.providerChip}
          />
        </Box>

        <Divider style={{ margin: '8px 0' }} />

        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileLabel}>App ID</Typography>
          <Typography className={classes.mobileValue}>{cred.app_id}</Typography>
        </Box>

        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileLabel}>Token Status</Typography>
          {renderStatusChip(cred.dp_user_id)}
        </Box>

        <Box className={classes.mobileCardRow}>
          <Typography className={classes.mobileLabel}>Updated</Typography>
          <Typography className={classes.mobileValue}>
            {formatDate(cred.updated_at)}
          </Typography>
        </Box>

        {tokenStatuses[cred.dp_user_id] !== 'ACTIVE' && (
          <Button
            variant="contained"
            color="primary"
            className={classes.loginButton}
            onClick={() => handleLogin(cred.dp_user_id)}
          >
            Login to Broker
          </Button>
        )}
      </CardContent>
    </Card>
  );

  // Desktop Table View
  const renderDesktopTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell>DP User ID</StyledTableCell>
            <StyledTableCell>Provider</StyledTableCell>
            <StyledTableCell>App ID</StyledTableCell>
            <StyledTableCell>Token Status</StyledTableCell>
            <StyledTableCell>Updated At</StyledTableCell>
            <StyledTableCell>Actions</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {credentials.map((cred) => (
            <TableRow key={cred.id} hover>
              <TableCell>{cred.dp_user_id}</TableCell>
              <TableCell>{cred.app_provider}</TableCell>
              <TableCell>{cred.app_id}</TableCell>
              <TableCell>{renderStatusChip(cred.dp_user_id)}</TableCell>
              <TableCell>{formatDate(cred.updated_at)}</TableCell>
              <TableCell>
                {tokenStatuses[cred.dp_user_id] !== 'ACTIVE' && (
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => handleLogin(cred.dp_user_id)}
                  >
                    Login
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Layout title="Credentials" onRefresh={fetchData}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Credentials" onRefresh={fetchData}>
      {!isAdmin && (
        <Typography variant="body2" className={classes.subtitle}>
          Your broker credentials
        </Typography>
      )}
      {credentials.length === 0 ? (
        <Paper className={classes.emptyMessage}>
          <Typography variant="body1">
            {isAdmin
              ? 'No broker credentials found in the system.'
              : 'No broker credentials linked to your account. Please contact admin to add your broker.'}
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Mobile View - Cards */}
          <Hidden mdUp>
            <Grid container>
              {credentials.map((cred) => (
                <Grid item xs={12} key={cred.id}>
                  {renderMobileCard(cred)}
                </Grid>
              ))}
            </Grid>
          </Hidden>

          {/* Desktop View - Table */}
          <Hidden smDown>
            {renderDesktopTable()}
          </Hidden>
        </>
      )}
    </Layout>
  );
}

export default Credentials;
